/**
 * H19: the horse and the bamboo log in every story. Origin A gets two quests inside the plains chain (`OA`, after the
 * bandits, see data/plainsOpening.ts); origins B, C and D each get a starter chain on their greybox town
 * (`ORIGIN_CHAINS`, run by systems/originStory.ts, tracked as `og-<id>` scene marks so the shared quest log is left
 * alone). Every chain: wake, the keeper, a fight in the pocket, the horse (one starter horse, mount it, ride to a
 * spot), ride out by the exit, then the bamboo log on the plains stream (one log, stand on it and pole across).
 * TODO-polish: all text is rough skeleton copy (Narrative to rewrite); names are original placeholders.
 */
import type { QuestDef } from '../quests';

export type XY = readonly [number, number];
export const STARTER_HORSE = 'horse-bay';
export const STARTER_LOG = 'bamboo-log';

/** Origin A's two quests (ids appended to PLAINS_CHAIN after the bandits). */
export const OA = { horse: 'plains-horse', log: 'plains-bamboo' } as const;

export interface StoryNpcDef {
  readonly role: 'keeper' | 'horsekeeper' | 'ferryman';
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  readonly at: XY;
}

/** A spot to reach: centre (tile) and radius (m); `mount` = must be riding it; `water` = must be on water. */
export interface RideSpot {
  readonly at: XY;
  readonly r: number;
  readonly mount?: string;
  readonly water?: boolean;
}

/** The plains' bank of the stream (a ferryman there for every origin) and the stretch to pole across. */
export const FERRYMAN: StoryNpcDef = { role: 'ferryman', name: 'ลุงพายไผ่', title: 'ผู้พายไม้ไผ่', sprite: 'stationMaster', at: [146.5, 122.5] }; // TODO-polish
export const LOG_SPOT: RideSpot = { at: [150, 138], r: 6, mount: STARTER_LOG, water: true };

/** Origin A: the horse-station keeper at the carriage stop (hs-carriage) and a ride to the stele road. */
export const HORSEKEEPER_A: StoryNpcDef = { role: 'horsekeeper', name: 'ลุงเลี้ยงม้า', title: 'ผู้ดูแลสถานีม้า', sprite: 'stationMaster', at: [112.5, 176.5] }; // TODO-polish
export const HORSE_SPOT_A: RideSpot = { at: [128.5, 146.5], r: 6, mount: STARTER_HORSE };

export const OA_QUESTS: readonly QuestDef[] = [
  {
    id: OA.horse,
    title: 'ม้าตัวแรก', // TODO-polish
    offer: 'ไปที่ท่ารถม้า ลุงเลี้ยงม้าจะสอนให้ขี่ม้าและยกม้าให้หนึ่งตัว', // TODO-polish
    remind: 'ลุงเลี้ยงม้าอยู่ที่ท่ารถม้า กดขึ้นม้าแล้วขี่ไปทางศิลากลางลาน', // TODO-polish
    done: 'ขี่ม้าเป็นแล้ว ม้าตัวนี้เป็นของเจ้า ใช้สถานีม้าฝากและเรียกมันได้', // TODO-polish
    objectives: [
      { type: 'talk', target: 'horsekeeper', count: 1, label: 'คุยกับลุงเลี้ยงม้า รับม้า' },
      { type: 'visit', target: 'horse-ride', count: 1, label: 'ขี่ม้าไปที่ถนนสายศิลา' },
    ],
    rewards: { cultivation: 300, gold: 60 },
    giver: { name: HORSEKEEPER_A.name, where: 'ที่ท่ารถม้า' },
  },
  {
    id: OA.log,
    title: 'ไผ่ลำเดียว', // TODO-polish
    offer: 'ที่ลำธารกลางทุ่ง ลุงพายไผ่จะแจกไม้ไผ่ให้หนึ่งท่อน ยืนบนมันแล้วพายข้ามน้ำ', // TODO-polish
    remind: 'ลุงพายไผ่อยู่ริมลำธาร ขึ้นไม้ไผ่แล้วพายไปกลางน้ำ', // TODO-polish
    done: 'ยืนพายไม้ไผ่ข้ามน้ำได้แล้ว', // TODO-polish
    objectives: [
      { type: 'talk', target: 'ferryman', count: 1, label: 'คุยกับลุงพายไผ่ รับไม้ไผ่ 1 ท่อน' },
      { type: 'visit', target: 'log-ride', count: 1, label: 'ยืนพายไม้ไผ่ไปกลางลำธาร' },
    ],
    rewards: { cultivation: 300, gold: 60 },
    giver: { name: FERRYMAN.name, where: 'ริมลำธารกลางทุ่ง' },
  },
];

export interface OriginChainDef {
  readonly origin: 'b' | 'c' | 'd';
  readonly zone: string;
  readonly keeper: StoryNpcDef;
  readonly horsekeeper: StoryNpcDef;
  readonly horseSpot: RideSpot;
  readonly exitSpot: RideSpot;
  readonly quests: readonly QuestDef[];
}

const EXIT: RideSpot = { at: [48.5, 4.5], r: 5, mount: STARTER_HORSE };
const HORSE_SPOT: RideSpot = { at: [66.5, 58.5], r: 5, mount: STARTER_HORSE };

function chain(o: 'b' | 'c' | 'd', place: string, keeperName: string, horseName: string): OriginChainDef {
  const q = (s: string) => `og-${o}-${s}`;
  const keeper: StoryNpcDef = { role: 'keeper', name: keeperName, title: 'ผู้ดูแลเมือง', sprite: 'stationMaster', at: [41.5, 47.5] }; // TODO-polish
  const horsekeeper: StoryNpcDef = { role: 'horsekeeper', name: horseName, title: 'ผู้ดูแลม้า', sprite: 'stationMaster', at: [55.5, 47.5] }; // TODO-polish
  const quests: QuestDef[] = [
    {
      id: q('wake'), title: 'ตื่นที่' + place, // TODO-polish
      offer: 'เจ้าตื่นขึ้นที่' + place, remind: 'ลองเดินดูรอบลานกลางเมือง', done: 'เจ้ายืนได้แล้ว', // TODO-polish
      objectives: [{ type: 'visit', target: q('arrive'), count: 1, label: 'ตื่นและก้าวเข้าลานกลางเมือง' }],
      rewards: { cultivation: 100, gold: 20 },
    },
    {
      id: q('keeper'), title: 'พบ' + keeperName, // TODO-polish
      offer: `ไปพบ${keeperName}ที่ลานกลางเมือง`, remind: `${keeperName}อยู่ทางตะวันตกของลาน`, done: `${keeperName}ต้อนรับเจ้า`, // TODO-polish
      objectives: [{ type: 'talk', target: 'keeper', count: 1, label: 'คุยกับ' + keeperName }],
      rewards: { cultivation: 150, gold: 30 },
      giver: { name: keeperName, where: 'ที่ลานกลางเมือง' },
    },
    {
      id: q('pocket'), title: 'หมาป่าชายเมือง', // TODO-polish
      offer: 'หมาป่าบุกมุมตะวันออกเฉียงใต้ของเมือง ช่วยกำจัดสามตัว', remind: 'หมาป่าอยู่นอกลาน มุมตะวันออกเฉียงใต้', done: 'ชายเมืองสงบแล้ว', // TODO-polish
      objectives: [{ type: 'kill', target: 'wolf', count: 3, label: 'หมาป่า' }],
      rewards: { cultivation: 300, gold: 60 },
      giver: { name: keeperName, where: 'ที่ลานกลางเมือง' },
    },
    {
      id: q('horse'), title: 'ม้าตัวแรก', // TODO-polish
      offer: `ไปหา${horseName}ที่คอกม้า จะสอนขึ้นม้าและยกม้าให้หนึ่งตัว`, remind: `${horseName}อยู่ทางตะวันออกของลาน ขึ้นม้าแล้วขี่ไปจุดหมาย`, // TODO-polish
      done: 'ขี่ม้าเป็นแล้ว ม้าตัวนี้เป็นของเจ้า', // TODO-polish
      objectives: [
        { type: 'talk', target: 'horsekeeper', count: 1, label: `คุยกับ${horseName} รับม้า` },
        { type: 'visit', target: q('horse-ride'), count: 1, label: 'ขี่ม้าไปที่ปลายถนนตะวันออก' },
      ],
      rewards: { cultivation: 300, gold: 60 },
      giver: { name: horseName, where: 'ที่คอกม้า' },
    },
    {
      id: q('exit'), title: 'ขี่ออกจากเมือง', // TODO-polish
      offer: 'ขี่ม้าไปที่ประตูเหนือ ถนนทางนั้นพาไปลานศิลาเก้ากระบี่', remind: 'ขี่ม้าไปที่ช่องกำแพงทางเหนือ', done: 'ถึงประตูเหนือแล้ว ทุ่งกว้างอยู่ข้างหน้า', // TODO-polish
      objectives: [{ type: 'visit', target: q('exit-ride'), count: 1, label: 'ขี่ม้าไปที่ประตูเหนือ' }],
      rewards: { cultivation: 300, gold: 60 },
    },
    {
      id: q('bamboo'), title: 'ไผ่ลำเดียว', // TODO-polish
      offer: OA_QUESTS[1]!.offer, remind: OA_QUESTS[1]!.remind, done: OA_QUESTS[1]!.done,
      objectives: OA_QUESTS[1]!.objectives,
      rewards: { cultivation: 300, gold: 60 },
      giver: OA_QUESTS[1]!.giver,
    },
  ];
  return { origin: o, zone: 'town-' + o, keeper, horsekeeper, horseSpot: HORSE_SPOT, exitSpot: EXIT, quests };
}

export const ORIGIN_CHAINS: Readonly<Record<'b' | 'c' | 'd', OriginChainDef>> = {
  b: chain('b', 'เหมืองเงาผา', 'ป้าเฝ้าเหมือง', 'ลุงม้าเหมือง'),
  c: chain('c', 'ท่าระฆังเงียบ', 'ครูเฝ้าระฆัง', 'ลุงม้าท่าเรือ'),
  d: chain('d', 'เมืองโคมเก้าสาย', 'เจ้าโรงงิ้ว', 'ลุงม้าโรงงิ้ว'),
};
